<template>
  <div v-if="show" class="tg-overlay">
    <div class="tg-modal">
      <div class="tg-header">
        <div>
          <h6 class="tg-title">Before you continue — Terms &amp; Privacy</h6>
          <div class="tg-sub">Please read fully. You must scroll to the end before accepting.</div>
        </div>
        <span class="tg-version" v-if="version">v{{ version }}</span>
      </div>

      <div class="tg-progress"><div class="tg-progress-bar" :style="{width: progress + '%'}"></div></div>

      <div ref="scrollBox" class="tg-scroll" @scroll="onScroll">
        <terms-content />
      </div>

      <div class="tg-footer">
        <div v-if="!scrolledToEnd" class="tg-warn"><i class="mdi mdi-arrow-down-bold-circle me-1"></i>Scroll to the end ({{ Math.round(progress) }}% read)</div>
        <div v-else-if="!dwellPassed" class="tg-warn"><i class="mdi mdi-clock-outline me-1"></i>Take a moment — {{ dwellRemaining }}s left</div>
        <div v-else class="tg-ready"><i class="mdi mdi-check-circle me-1"></i>You may now accept</div>

        <label class="tg-check">
          <input type="checkbox" v-model="agreed" :disabled="!canAgree" />
          <span>I have read and agree to the Flockr Terms of Service and Privacy Policy.</span>
        </label>

        <div class="d-flex gap-2 mt-3">
          <button class="btn btn-warning fw-bold flex-fill" :disabled="!agreed || submitting" @click="accept">
            <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span>Recording…</span>
            <span v-else><i class="mdi mdi-check me-1"></i>I accept</span>
          </button>
          <button class="btn btn-outline-light" @click="decline">Decline &amp; sign out</button>
        </div>
        <div v-if="error" class="tg-error mt-2">{{ error }}</div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import TermsContent from "@/components/flockr-legal/TermsContent.vue";
import { executeJson, flocker } from "@/api";

export default {
  name: "TermsGate",
  components: { TermsContent },
  data(){return{
    show:false, submitting:false, error:'',
    version:'',
    scrolledToEnd:false, progress:0,
    dwellSeconds:0, dwellPassed:false, dwellTimer:null,
    minDwell:5, // seconds before checkbox unlocks
    agreed:false,
    startedAt:0
  }},
  computed:{
    canAgree(){ return this.scrolledToEnd && this.dwellPassed; },
    dwellRemaining(){ return Math.max(0, this.minDwell - this.dwellSeconds); }
  },
  mounted(){
    this.checkStatus();
    var self=this;
    this._onLogin=function(){ self.checkStatus(); };
    window.addEventListener('flockr-login-success', this._onLogin);
  },
  beforeUnmount(){
    if (this.dwellTimer) clearInterval(this.dwellTimer);
    if (this._onLogin) window.removeEventListener('flockr-login-success', this._onLogin);
  },
  methods:{
    async checkStatus(){
      var userId = sessionStorage.getItem('userId') || '';
      if (!userId) { this.show = false; return; }
      try {
        var r = await executeJson({function:'getTermsStatus', userId: userId}, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.version = r.data.data.currentVersion || '';
          if (!r.data.data.accepted) {
            this.show = true;
            this.startedAt = Date.now();
            this.startDwellTimer();
          } else {
            this.show = false;
          }
        }
      } catch(e){}
    },
    startDwellTimer(){
      var self=this;
      this.dwellSeconds=0; this.dwellPassed=false;
      if (this.dwellTimer) clearInterval(this.dwellTimer);
      this.dwellTimer = setInterval(function(){
        self.dwellSeconds++;
        if (self.dwellSeconds >= self.minDwell) {
          self.dwellPassed = true;
          clearInterval(self.dwellTimer);
        }
      }, 1000);
    },
    onScroll(e){
      var el = e.target;
      var max = el.scrollHeight - el.clientHeight;
      if (max <= 0) { this.progress = 100; this.scrolledToEnd = true; return; }
      var pct = (el.scrollTop / max) * 100;
      this.progress = Math.min(100, pct);
      // Mark "scrolled to end" once they're within 30px of bottom
      if ((el.scrollHeight - el.scrollTop - el.clientHeight) < 30) this.scrolledToEnd = true;
    },
    async accept(){
      if (!this.canAgree || !this.agreed) return;
      this.submitting = true; this.error = '';
      var dwell = Math.round((Date.now() - this.startedAt) / 1000);
      try {
        var r = await executeJson({
          function:'acceptTerms',
          userId: sessionStorage.getItem('userId')||'',
          version: this.version,
          scrolledToEnd: 1,
          dwellSeconds: dwell
        }, flocker);
        if (r.data && r.data.success) {
          this.show = false;
          window.dispatchEvent(new CustomEvent('flockr-terms-accepted'));
        } else {
          this.error = (r.data && r.data.message) || 'Could not record acceptance';
        }
      } catch(e){ this.error = 'Network error'; }
      this.submitting = false;
    },
    decline(){
      // Hard sign-out — they cannot use the platform without accepting
      try { sessionStorage.clear(); localStorage.removeItem('authToken'); } catch(e){}
      this.show = false;
      this.$router.push('/login');
    }
  }
};
</script>

<style scoped>
.tg-overlay{position:fixed;inset:0;background:rgba(0,0,0,.85);z-index:10000;display:flex;align-items:center;justify-content:center;padding:14px;}
.tg-modal{width:100%;max-width:720px;max-height:92vh;background:#141414;border:1px solid #BFA164;border-radius:14px;color:#F5F0E8;display:flex;flex-direction:column;overflow:hidden;}
html[data-theme="light"] .tg-modal{background:#FFFFFF;color:#1A1A1A;border-color:#BFA164;}
.tg-header{padding:18px 22px 12px;display:flex;align-items:flex-start;justify-content:space-between;gap:8px;}
.tg-title{margin:0;font-size:16px;font-weight:800;color:#BFA164}
.tg-sub{color:var(--fm-s,#6B6356);font-size:11px;margin-top:2px}
.tg-version{color:var(--fm-s,#6B6356);font-size:11px;background:rgba(191,161,100,.1);padding:2px 8px;border-radius:8px;flex-shrink:0}
.tg-progress{height:3px;background:rgba(255,255,255,.06)}
.tg-progress-bar{height:100%;background:#BFA164;transition:width .3s ease}
.tg-scroll{flex:1;overflow-y:auto;padding:6px 22px 14px;border-top:1px solid var(--fm-br,#1E1E1E);border-bottom:1px solid var(--fm-br,#1E1E1E);}
html[data-theme="light"] .tg-scroll{border-color:#E8E2DA;}
.tg-footer{padding:14px 22px 18px}
.tg-warn{color:#FF9800;font-size:12px;font-weight:600;margin-bottom:10px;display:flex;align-items:center}
.tg-ready{color:#50C878;font-size:12px;font-weight:600;margin-bottom:10px;display:flex;align-items:center}
.tg-check{display:flex;align-items:flex-start;gap:8px;font-size:13px;cursor:pointer}
.tg-check input{margin-top:3px}
.tg-check input:disabled+span{color:var(--fm-s,#6B6356);}
.tg-error{color:#FF6B6B;font-size:12px}
</style>
